import { useState } from 'react';
import { englishLevelMeta, type EnglishLevel } from '@/mocks/englishWords';
import { englishTypeMeta, ENGLISH_TYPE_COUNT } from '@/lib/englishQuestions';

interface Props {
  onStart: (level: EnglishLevel) => void;
}

export default function EnglishIntro({ onStart }: Props) {
  const [level, setLevel] = useState<EnglishLevel>('elementary');

  const total = Object.values(ENGLISH_TYPE_COUNT).reduce((a, b) => a + b, 0);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Hero Card */}
      <div className="bg-accent-500 rounded-2xl p-8 md:p-10 text-white text-center mb-6">
        <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-5">
          <i className="ri-book-open-line text-3xl text-white"></i>
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-3">
          영어 단어 검사
        </h1>
        <p className="text-accent-100 text-sm md:text-base leading-relaxed">
          5가지 유형의 영단어 문제로 나의 영어 어휘 수준을 정밀하게 진단합니다.
          <br className="hidden md:block" />
          결과는 상위·하위 백분위로 확인할 수 있어요.
        </p>
      </div>

      {/* Level Selection */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-list-check-3 text-accent-500"></i>
          검사 레벨 선택
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {englishLevelMeta.map((l) => {
            const active = level === l.id;
            return (
              <button
                key={l.id}
                onClick={() => setLevel(l.id)}
                className={`text-left rounded-lg border p-4 transition-colors cursor-pointer ${
                  active
                    ? 'border-accent-500 bg-accent-50 ring-2 ring-accent-300'
                    : 'border-background-200 bg-white hover:border-accent-300'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg mb-2 ${active ? 'bg-accent-500 text-white' : 'bg-accent-100 text-accent-600'}`}>
                  <i className={`${l.icon}`}></i>
                </div>
                <div className="font-semibold text-foreground-950 text-sm">{l.label}</div>
                <div className="text-xs text-foreground-500 mt-1 leading-relaxed">{l.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Question Type Info */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-layout-grid-line text-primary-500"></i>
          검사 구성 (총 {total}문항)
        </h3>
        <div className="space-y-2">
          {englishTypeMeta.map((t) => (
            <div key={t.id} className="flex items-center gap-3 border border-background-200 rounded-lg p-3">
              <div className="w-9 h-9 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                <i className={`${t.icon} text-primary-600`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-foreground-900 text-sm">{t.label}</div>
                <div className="text-xs text-foreground-500 mt-0.5">{t.description}</div>
              </div>
              <span className="text-sm font-bold text-accent-600 whitespace-nowrap">{ENGLISH_TYPE_COUNT[t.id]}문항</span>
            </div>
          ))}
        </div>
      </div>

      {/* Notice */}
      <div className="bg-background-100 border border-background-200 rounded-xl p-4 mb-6">
        <div className="flex gap-2">
          <i className="ri-information-line text-foreground-500 flex-shrink-0 mt-0.5"></i>
          <p className="text-foreground-600 text-sm leading-relaxed">
            <strong>안내:</strong> 이 검사는 참고용 어휘 진단 검사입니다.
            학년별 단어 난이도를 반영해 상위·하위 백분위를 산출하며, 정식 영어능력시험을 대체하지 않습니다.
          </p>
        </div>
      </div>

      <button
        onClick={() => onStart(level)}
        className="whitespace-nowrap w-full bg-accent-500 hover:bg-accent-600 text-white font-bold py-4 rounded-xl text-base transition-colors cursor-pointer"
      >
        {englishLevelMeta.find((l) => l.id === level)?.label} 검사 시작하기{' '}
        <i className="ri-arrow-right-line ml-2"></i>
      </button>
    </div>
  );
}